// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Designations page (Stage HRMS-1).
// COMPACT INLINE CRUD with an optional Department link per row.
// RBAC server-side (hrms.designation.*).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Select, Switch, Space, Spin, Table, Tooltip, message } from 'antd';
import {
  PlusOutlined, SaveOutlined, ReloadOutlined, IdcardOutlined, SearchOutlined
} from '@ant-design/icons';

import { hrDesignationsApi } from './hrDesignationsApi';
import { hrDepartmentsApi } from '../departments/hrDepartmentsApi';
import {
  normalizeDesignationRows, sortDesignations, validateDesignationRows,
  buildDesignationBulkPayload, markDirtyRow
} from './hrDesignationsHelpers';
import '../hrms.css';

export default function DesignationsPage() {
  const [rows, setRows]       = useState([]);
  const [depts, setDepts]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dOut, depOut] = await Promise.all([
        hrDesignationsApi.list({ limit: 1000 }),
        hrDepartmentsApi.lookups()
      ]);
      setRows(sortDesignations(normalizeDesignationRows((dOut && dOut.rows) || [])));
      setDepts(((depOut && depOut.rows) || []).map((d) => ({ value: d.id, label: d.departmentName })));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load designations.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.designationCode, r.designationName, r.departmentName]
      .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(() => rows.filter((r) => r._dirty).length, [rows]);
  const patch = (clientRowId, p) => setRows((prev) => markDirtyRow(prev, clientRowId, p));

  const addRow = () => {
    setRows((prev) => [...prev, {
      _clientRowId: `hg-new-${Date.now()}-${prev.length + 1}`, _dirty: true,
      id: null, designationCode: '', designationName: '', departmentId: null, departmentName: null, active: true
    }]);
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (dirty.length === 0) { message.info('Nothing to save.'); return; }
    const { ok, errors } = validateDesignationRows(rows);
    if (!ok) { message.error(errors[0].message); return; }
    setSaving(true);
    try {
      const out = await hrDesignationsApi.bulkSave(buildDesignationBulkPayload(rows, []));
      message.success(`Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save designations.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Designation Code', dataIndex: 'designationCode', width: 200,
      render: (v, row) => (
        <Input size="small" variant="borderless" value={v} placeholder="e.g., SALESEXE"
          maxLength={40} onChange={(e) => patch(row._clientRowId, { designationCode: e.target.value })} />
      ) },
    { title: 'Designation Name', dataIndex: 'designationName', width: 280,
      render: (v, row) => (
        <Input size="small" variant="borderless" value={v} placeholder="e.g., Sales Executive"
          maxLength={160} onChange={(e) => patch(row._clientRowId, { designationName: e.target.value })} />
      ) },
    { title: 'Department', dataIndex: 'departmentId', width: 220,
      render: (v, row) => (
        <Select size="small" variant="borderless" allowClear showSearch optionFilterProp="label"
          value={v || undefined} style={{ width: '100%' }} placeholder="(optional)"
          options={depts}
          onChange={(val) => patch(row._clientRowId, { departmentId: val || null })} />
      ) },
    { title: 'Active', dataIndex: 'active', width: 90, align: 'center',
      render: (v, row) => (
        <Switch size="small" checked={v !== false}
          onChange={(checked) => patch(row._clientRowId, { active: checked })} />
      ) }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><IdcardOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Designations</span>
            <span className="hr-headstrip__subtitle">
              Edit any cell directly — Department is optional. Save All Changes to persist.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="hr-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && (
            <span className="hr-stat-chip hr-stat-chip--dirty"><strong>{dirtyCount}</strong> unsaved</span>
          )}
        </Space>
      </div>

      <div className="hr-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search code, name, department…"
          value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 320 }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Add Row</Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveAll}
          loading={saving} disabled={saving || dirtyCount === 0}>Save All Changes</Button>
      </div>

      <div className="hr-card">
        <Spin spinning={loading}>
          <Table className="hr-grid" size="small" rowKey="_clientRowId"
            dataSource={visible} columns={columns} pagination={false} sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'hr-dirty-row' : ''}
            locale={{ emptyText: rows.length === 0
              ? 'No designations yet. Click Add Row to start.'
              : 'No designations match the current search.' }} />
        </Spin>
      </div>
    </div>
  );
}
